import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import type { Site } from '@hotjar/site';
import {
  startVerification,
  getVerificationStatus,
  openVerificationPopup,
} from '@hotjar/webapp/domains/activation/tracking-code/verification';
import type {
  TrackingCodeState,
  VerificationResult,
} from '@hotjar/webapp/domains/activation/tracking-code/types';
import { VerificationStatus } from '@hotjar/webapp/domains/activation/tracking-code/types';
import { getStateFromServer } from '@hotjar/webapp/domains/activation/tracking-code';
import { useTrackingCode } from './useTrackingCode';
import { useTrack } from './useTrack';

const MAX_RETRIES = 30;
const RETRIES_INTERVAL_MS = 2000;

function useTrackingCodeVerificationStart(url: string, site: Site) {
  const { getState } = useTrackingCode();
  const [enabled, setEnabled] = useState(false);
  const shouldAutogenerateRecording = !getState(site?.lastData).installed;
  const { trackTCVerificationRequest } = useTrack();

  useEffect(() => {
    if (url && site) {
      setEnabled(true);
    } else {
      setEnabled(false);
    }
  }, [url, site, setEnabled]);

  return useQuery(
    'tracking-code-verification-start',
    () => startVerification(url, shouldAutogenerateRecording),
    {
      enabled,
      cacheTime: 0,
      retry: false,
      onSuccess: () => {
        setEnabled(false);
        trackTCVerificationRequest(site);
      },
      onError: () => {
        setEnabled(false);
        trackTCVerificationRequest(site);
      },
    },
  );
}

function useTrackingCodeVerificationResult(uuid: string | undefined, enabled: boolean) {
  const [retries, setRetries] = useState(0);

  useEffect(() => {
    if (uuid && enabled) {
      setRetries(0);
    }
  }, [uuid, enabled]);

  return useQuery(
    'tracking-code-verification-result',
    () => getVerificationStatus(uuid as string),
    {
      cacheTime: 0,
      retry: false,
      refetchInterval: RETRIES_INTERVAL_MS,
      refetchIntervalInBackground: true,
      enabled: enabled && uuid !== undefined && retries <= MAX_RETRIES,
      select: (data: VerificationResult) => {
        return retries < MAX_RETRIES
          ? data
          : {
              status: VerificationStatus.TIMEOUT,
            };
      },
      onSuccess: () => {
        if (enabled) {
          setRetries(retries + 1);
        }
      },
    },
  );
}

function useBrowserVerification(site: Site, enabled: boolean) {
  const [retries, setRetries] = useState(0);

  useEffect(() => {
    if (site && enabled) {
      setRetries(0);
    }
  }, [site, enabled]);

  return useQuery('tracking-code-refresh-state', () => getStateFromServer(site), {
    cacheTime: 0,
    retry: false,
    refetchInterval: RETRIES_INTERVAL_MS,
    refetchIntervalInBackground: true,
    enabled: enabled && retries <= MAX_RETRIES,
    select: (state: TrackingCodeState) => {
      if (state.installed && state.isActive) {
        return { status: VerificationStatus.SUCCESS };
      }

      if (retries < MAX_RETRIES) {
        return { status: VerificationStatus.PENDING };
      }

      return { status: VerificationStatus.TIMEOUT };
    },
    onSuccess: () => {
      if (enabled) {
        setRetries(retries + 1);
      }
    },
  });
}

export function useTrackingCodeVerification(url: string, site: Site): VerificationStatus {
  const { trackTCVerificationAttempt } = useTrack();
  const [popup, setPopup] = useState<Window | null>();
  const [status, setStatus] = useState(VerificationStatus.PENDING);
  const isPending = status === VerificationStatus.PENDING;
  const { data: uuid, isLoading: isVerificationStartPending } = useTrackingCodeVerificationStart(
    url,
    site,
  );
  const { data: verificationResult } = useTrackingCodeVerificationResult(uuid, isPending);
  const { data: browserVerificationResult, isError: browserVerificationError } =
    useBrowserVerification(site, isPending);

  useEffect(() => {
    if (!isVerificationStartPending) {
      setPopup(openVerificationPopup(url, site, uuid));
    }
  }, [uuid, isVerificationStartPending, site, url]);

  useEffect(() => {
    if (isPending && verificationResult) {
      setStatus(verificationResult.status);
    }
  }, [setStatus, verificationResult, isPending]);

  useEffect(() => {
    if (isPending && browserVerificationResult) {
      setStatus(browserVerificationResult.status);
    }
  }, [isPending, browserVerificationResult, setStatus]);

  useEffect(() => {
    if (browserVerificationError) {
      setStatus(VerificationStatus.UNDEFINED_FAILURE);
    }
  }, [browserVerificationError, setStatus]);

  useEffect(() => {
    if (status !== VerificationStatus.PENDING) {
      popup?.close();
      trackTCVerificationAttempt(site, status);
    }
  }, [popup, status, site, trackTCVerificationAttempt]);

  return status;
}
